import React, { useState, useEffect } from "react";
import { useUserProfile } from "../../../hooks/useUserProfile";
import StripeCheckout from "./StripeCheckout";
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { Button, Modal, Input, Loading } from 'react-daisyui';
import { GraduationCap, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePotentialMatchCount } from "../../../hooks/usePotentialMatchCount";
import { createPortal } from "react-dom";
import { trackTrialSubscriptionConversion } from "../../../modules/analytics/googleAds";

export const useIsSubscriptionManagementValid = () => {
    const { subscriptionData } = useUserProfile();
    return subscriptionData?.isSubscribed === true;
};

const SubscriptionManagement: React.FC = () => {
    const { t } = useTranslation();
    const { userProfile, updateUserProfile, subscriptionData, localProfile } = useUserProfile();
    const [couponCode, setCouponCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const redirectStatus = searchParams.get('redirect_status');
    const setupIntentId = searchParams.get('setup_intent');
    const hasSuccessfulPaymentRedirect = redirectStatus === 'succeeded';
    const { matchCount } = usePotentialMatchCount(localProfile ?? {});

    if (userProfile === undefined) {
        return <Loading />; // or any other loading indicator
    }

    useEffect(() => {
        if (hasSuccessfulPaymentRedirect) {
            trackTrialSubscriptionConversion(window.sessionStorage, {
                redirectStatus,
                setupIntentId,
            });
            handlePaymentSuccess();
        }
    }, [hasSuccessfulPaymentRedirect, redirectStatus, setupIntentId]);

    const handlePaymentSuccess = () => {
        setPaymentSuccess(true);
        setIsDialogOpen(false);
    };

    const handleCancelSubscription = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                throw new Error('User not authenticated');
            }

            const { data, error } = await supabase.functions.invoke('create-subscription', {
                body: JSON.stringify({
                    action: 'cancel',
                    userId: user.id
                })
            });

            if (error) {
                throw error;
            }

            if (data.message === "Subscription cancelled successfully") {
                alert('Ihr Abonnement wurde erfolgreich gekündigt.');
            } else {
                throw new Error('Unexpected response from server');
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            setError('Fehler beim Kündigen des Abonnements. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsCancelDialogOpen(false);
        }
    };

    const planType = userProfile.budget_preferences?.max && userProfile.budget_preferences.max <= 850 ? 'student' : 'standard';

    const renderSubscriptionStatus = () => {
        if (subscriptionData.subscription_status === 'cancelling' || subscriptionData.subscription_status === 'cancelled') {
            return (
                <div className="flex flex-col items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h2 className="text-2xl font-bold text-center text-error">
                        {t('profilePages.subscriptionManagement.cancelledSubscription.title')}
                    </h2>
                    <p className="text-center">
                        {t('profilePages.subscriptionManagement.cancelledSubscription.message')}
                    </p>
                    <Button color="primary" onClick={() => setIsDialogOpen(true)}>
                        {t('profilePages.subscriptionManagement.cancelledSubscription.renewSubscription')}
                    </Button>
                </div>
            );
        } else if (subscriptionData.isSubscribed) {
            return (
                <div className="flex flex-col items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h2 className="text-2xl font-bold text-center text-success">
                        {t('profilePages.subscriptionManagement.activeSubscription.congratulations')}
                    </h2>
                    <p className="text-center">
                        {t('profilePages.subscriptionManagement.activeSubscription.currentPlan', { plan: subscriptionData.subscription_type === 'student' ? t('profilePages.subscriptionManagement.planTypes.student') : t('profilePages.subscriptionManagement.planTypes.standard') })}
                    </p>
                    <p className="text-center">
                        {t('profilePages.subscriptionManagement.activeSubscription.enjoyBenefits')}
                    </p>
                    <p className="text-center font-bold mt-4">
                        {t('profilePages.subscriptionManagement.activeSubscription.offersComingSoon')}
                    </p>
                    <Button color="ghost" onClick={() => setIsCancelDialogOpen(true)}>
                        {t('profilePages.subscriptionManagement.activeSubscription.cancelSubscription')}
                    </Button>
                    <Modal open={isCancelDialogOpen} >
                        <Modal.Header>{t('profilePages.subscriptionManagement.cancelDialog.title')}</Modal.Header>
                        <Modal.Body>
                            {t('profilePages.subscriptionManagement.cancelDialog.message')}
                        </Modal.Body>
                        <Modal.Actions>
                            <Button color="ghost" onClick={() => setIsCancelDialogOpen(false)}>{t('profilePages.subscriptionManagement.cancelDialog.cancel')}</Button>
                            <Button color="error" onClick={handleCancelSubscription}>{t('profilePages.subscriptionManagement.cancelDialog.confirm')}</Button>
                        </Modal.Actions>
                    </Modal>
                </div>
            );
        } else {
            return null; // For 'none' or 'pending' status, show the default subscription selection UI
        }
    };

    const [showStripeCheckout, setShowStripeCheckout] = useState(false);

    const handleStartCheckout = () => {
        setShowStripeCheckout(true);
    };

    const handleCancelCheckout = () => {
        setShowStripeCheckout(false);
    };

    return (
        <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold mb-6">{t('profilePages.subscriptionManagement.title')}</h1>
            <div className="flex flex-col gap-4">
                {isLoading ? (
                    <div className="flex flex-col items-center gap-2">
                        <Loading size="lg" />
                        <p className="text-xl font-bold">{t('profilePages.subscriptionManagement.loading.title')}</p>
                        <p>{t('profilePages.subscriptionManagement.loading.message')}</p>
                    </div>
                ) : paymentSuccess || subscriptionData?.isSubscribed || subscriptionData?.subscription_status === 'cancelled' ? (
                    renderSubscriptionStatus()
                ) : showStripeCheckout ? (
                    <div className="flex flex-col gap-4">
                        <StripeCheckout
                            couponCode={couponCode}
                            planType={planType}
                            onSuccess={handlePaymentSuccess}
                        />
                        <Button
                            color="ghost"
                            onClick={handleCancelCheckout}
                            className="max-w-xs mx-auto mt-4"
                        >
                            {t('profilePages.subscriptionManagement.cancelDialog.cancel')}
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {/* matchCount && matchCount > 0 && (
                            <p className="text-center font-semibold">
                                {t('profilePages.subscriptionManagement.matchCountMessage', { count: matchCount })}
                            </p>
                        )*/}

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <div className={`flex flex-col items-center p-4 border-2 rounded-lg ${
                                planType === 'student' ? 'border-primary text-primary' : 'border-gray-300 text-gray-400'
                            }`}>
                                <GraduationCap size={48} className="mb-2" />
                                <span className="font-bold text-xl">{t('profilePages.subscriptionManagement.planTypes.student')}</span>
                                <span>{t('profilePages.subscriptionManagement.trialPeriod')}</span>
                                <span>{t('profilePages.subscriptionManagement.studentPrice')}</span>
                            </div>
                            <div className={`flex flex-col items-center p-4 border-2 rounded-lg ${
                                planType === 'standard' ? 'border-primary text-primary' : 'border-gray-300 text-gray-400'
                            }`}>
                                <Users size={48} className="mb-2" />
                                <span className="font-bold text-xl">{t('profilePages.subscriptionManagement.planTypes.standard')}</span>
                                <span>{t('profilePages.subscriptionManagement.trialPeriod')}</span>
                                <span>{t('profilePages.subscriptionManagement.standardPrice')}</span>
                                <span>{t('profilePages.subscriptionManagement.unlimitedBudget')}</span>
                            </div>
                        </div>

                        {planType !== 'student' && (
                            <p className="text-center">
                                {t('profilePages.subscriptionManagement.studentPlanWarning')}
                            </p>
                        )}

                        <p className="text-sm text-center">
                            {t('profilePages.subscriptionManagement.cancellationInfo')}
                        </p>
                        <Input
                            type="text"
                            placeholder={t('profilePages.subscriptionManagement.couponPlaceholder')}
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            className="max-w-xs mx-auto"
                        />
                        <Button
                            color="primary"
                            onClick={handleStartCheckout}
                            className="max-w-xs mx-auto"
                        >
                            {t('profilePages.subscriptionManagement.startTrialButton')}
                        </Button>
                        <p className="text-sm italic mt-2 text-center">
                            {t('profilePages.subscriptionManagement.trialDialog.cancellationInfo')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionManagement;